import classNames from "@/utils/classNames";
import {
    forwardRef,
    InputHTMLAttributes,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

const DEFAULT_MAX_LENGTH = 255;

export default forwardRef(function TextInput(
    {
        type = "text",
        className = "",
        isFocused = false,
        maxLength = DEFAULT_MAX_LENGTH,
        ...props
    }: InputHTMLAttributes<HTMLInputElement> & { isFocused?: boolean },
    ref,
) {
    const localRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
        focus: () => localRef.current?.focus(),
    }));

    useEffect(() => {
        if (isFocused) {
            localRef.current?.focus({ preventScroll: true });
        }

        // There might be a slight chance that the ref won't be available immediately,
        // But I don't want to put it as dependency on the value because it will cause duplicated renders.
        if (localRef.current?.value) {
            checkMaxLength(localRef.current?.value);
        }
    }, []);

    const [showMaxLength, setShowMaxLength] = useState(false);
    const checkMaxLength = (value: string) => {
        const percentage = (value.length / (maxLength ?? 0)) * 100;

        if (maxLength && percentage >= 80) {
            setShowMaxLength(true);
        } else {
            setShowMaxLength(false);
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange?.(e);
        checkMaxLength(e.target.value);
    };

    return (
        <div className="flex flex-col">
            <input
                {...props}
                type={type}
                maxLength={maxLength}
                onChange={onChange}
                className={classNames(
                    "rounded-md py-2 px-3 outline-offset-1 border border-brand-border text-brand-gray shadow-xs focus:outline-brand-red focus:border-brand-red",
                    props.disabled ? "bg-gray-100" : "bg-brand-white",
                    className,
                )}
                ref={localRef}
            />
            {showMaxLength && (
                <p className="self-end text-xs text-brand-gray pt-1">
                    {props.value?.toString().length}/{maxLength}
                </p>
            )}
        </div>
    );
});
